import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { apiFetch } from '../api-client';

// No secretAccessKey: the API never returns it (write-only by design).
export type Endpoint = {
  id: string;
  name: string;
  endpointUrl: string;
  bucket: string;
  region: string;
  forcePathStyle: boolean;
  accessKeyId: string;
  createdAt: string;
  tenantCount: number;
};

export type EndpointInput = {
  name: string;
  endpointUrl: string;
  bucket: string;
  accessKeyId: string;
  secretAccessKey?: string; // omitted on edit = keep the stored secret
  region?: string;
  forcePathStyle?: boolean;
};

export const endpointsQueryOptions = () =>
  queryOptions({
    queryKey: ['storage-endpoints'],
    queryFn: async () => {
      const res = await apiFetch('/api/console/storage-endpoints');
      if (!res.ok) throw new Error('Failed to load storage endpoints');
      return (await res.json()) as { endpoints: Endpoint[] };
    },
  });

export const tenantEndpointQueryOptions = (tenantId: string) =>
  queryOptions({
    queryKey: ['tenant-storage-endpoint', tenantId],
    queryFn: async () => {
      const res = await apiFetch(
        `/api/console/storage-endpoints/tenants/${tenantId}`,
      );
      if (!res.ok) throw new Error('Failed to load tenant storage endpoint');
      return (await res.json()) as {
        tenantId: string;
        endpointId: string | null;
      };
    },
  });

export function useCreateEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: EndpointInput) => {
      const res = await apiFetch('/api/console/storage-endpoints', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error('Failed to create endpoint');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['storage-endpoints'] }),
  });
}

export function useUpdateEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: EndpointInput & { id: string }) => {
      const res = await apiFetch(`/api/console/storage-endpoints/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error('Failed to update endpoint');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['storage-endpoints'] }),
  });
}

export function useDeleteEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/api/console/storage-endpoints/${id}`, {
        method: 'DELETE',
      });
      // 400 = still assigned to tenants; surface the server's reason.
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to delete endpoint');
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['storage-endpoints'] }),
  });
}

export function useAssignEndpoint(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (endpointId: string | null) => {
      const res = await apiFetch(
        `/api/console/storage-endpoints/tenants/${tenantId}`,
        {
          method: 'PUT',
          body: JSON.stringify({ endpointId }),
        },
      );
      if (!res.ok) throw new Error('Failed to assign endpoint');
      return res.json();
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['tenant-storage-endpoint', tenantId] }),
  });
}
